/**
 * levelgen/tutorial.ts — The town the guided walk is taught on.
 *
 * Level 1 is where somebody finds out what this game is. It is still a level
 * like any other — the same generator, the same seed it has always had
 * (`walk-1`), the same validator — but the two things that can end a run are
 * turned down to one each and moved out of the opening:
 *
 *   traffic   one vehicle, half a minute in, on a two-minute loop
 *   wildlife  one squirrel, twenty seconds in
 *
 * ## Why calm the town rather than empty it
 *
 * Both are still there on purpose. A tutorial that never shows a car teaches a
 * walk that does not exist: the horn, the frenzy meter and the two ways to lose
 * are the game, and meeting each of them once, late, with a card explaining it
 * (see `systems/tutorial.ts`) is the whole point of a guided walk. What is
 * removed is the *pressure* — nothing arrives while the player is still finding
 * the arrow keys, and nothing arrives in twos.
 *
 * ## Why it is built from the generated town rather than typed out
 *
 * Everything a hand-written town would have to get right — a reachable bin,
 * grass the dog can actually be walked to, houses whose doors open onto a
 * pavement, a home plot you can get back into — is already guaranteed for
 * `walk-1` by the generator and re-proved by the validator. Editing the two
 * schedules keeps all of that and changes only what is being changed. The level
 * is then **re-rated and re-validated**, because a level whose traffic was
 * edited by hand is a level whose label is a claim again rather than a fact.
 */
import { generateLevel, LevelGenerationError } from './generate';
import { rateLevel } from './difficulty';
import { isPlayable, validateLevel } from './validate';
import type { CritterRelease, Difficulty, HazardSpec, LevelSpec, TrafficSpec } from './types';

/** The seed level 1 has always been built from. Fixed forever — see campaign.ts. */
export const TUTORIAL_SEED = 'walk-1';

/** The band it is generated for, and still has to rate as once calmed. */
export const TUTORIAL_BAND: Difficulty = 'easy';

/**
 * When the schedule's one vehicle is released, and how long before the loop
 * brings another.
 *
 * Deliberately late and deliberately rare, because **this is not the car the
 * guided walk teaches with**. That one is aimed rather than scheduled: the
 * tutorial script sends it down the street as the walker carries the bag up to
 * the crossing (`systems/tutorial.ts` → `release-vehicle`), which is the only
 * way to be sure the lesson has its car in it. This is the backstop for a
 * player who ignores the cards entirely and takes their time — and with
 * `maxVehicles` at one, it can never arrive on top of the taught one.
 */
const FIRST_CAR_MS = 75_000;
const TRAFFIC_LOOP_MS = 120_000;

/**
 * When the squirrel wanders in.
 *
 * Around the point the script is explaining what critters do to a dog, so the
 * briefing has something to be about — and after the opening lessons, so the
 * first thing a new player meets is not a lunge.
 */
const CRITTER_AT_MS = 16_000;

/**
 * Build the guided-walk town.
 *
 * Deterministic, like every campaign level: same seed, same generator, same two
 * edits, same town on every machine. Throws if the result does not validate,
 * which would mean the edits below had broken something the generator promised —
 * a caller cannot sensibly carry on from that, and `tutorial.test.ts` is what
 * stops it ever reaching a player.
 */
export function tutorialLevel(): LevelSpec {
  return calm(generateLevel({ seed: TUTORIAL_SEED, difficulty: TUTORIAL_BAND }));
}

/**
 * Turn a generated town into a guided walk.
 *
 * Split out from the seed above so the *calming* can be tested against a town
 * chosen by the test rather than only the one the campaign happens to use —
 * including the towns where it must refuse. It is also the operation you would
 * apply if the guided walk were ever moved to a different seed.
 */
export function calm(town: LevelSpec): LevelSpec {
  const level: LevelSpec = {
    ...town,
    tutorial: true,
    traffic: oneVehicle(town.traffic),
    hazards: oneSquirrel(town.hazards),
  };
  // Re-measured from the level as edited, not inherited: the rating is the only
  // thing standing behind the word on the menu button.
  level.rating = rateLevel(level);

  const issues = validateLevel(level);
  if (!isPlayable(issues)) {
    throw new LevelGenerationError(
      `the tutorial town no longer validates — ${issues
        .filter((i) => i.severity === 'error')
        .map((i) => `${i.code}: ${i.message}`)
        .join('; ')}`,
      issues,
    );
  }
  return level;
}

/**
 * Keep one release out of the level's whole schedule, and move it late.
 *
 * The road and direction are the generator's own, so the vehicle still comes
 * down a street that crosses the map — a car released onto a cul-de-sac or a
 * cobbled lane is exactly what the validator rejects, and picking a road by hand
 * here is how that would happen.
 */
function oneVehicle(traffic: TrafficSpec): TrafficSpec {
  const first = traffic.schedule[0];
  return {
    maxVehicles: 1,
    speed: traffic.speed,
    // A car rather than whatever was scheduled: a van is the wider, harder body
    // to dodge, and the one vehicle a player ever meets here is the one they
    // are learning what a vehicle does from.
    schedule: first ? [{ ...first, atMs: FIRST_CAR_MS, kind: 'car' }] : [],
    loopMs: TRAFFIC_LOOP_MS,
  };
}

/**
 * One squirrel, where the level's own last critter was going to appear.
 *
 * Its position is kept rather than chosen: the generator already placed it off
 * the buildings and away from the doorstep, and the latest arrival is the one
 * furthest from the start of the walk in time as well as space. A squirrel
 * because it is the mildest of the three — the same lesson as a fox, without a
 * tangled lead on top of it.
 */
function oneSquirrel(hazards: HazardSpec): HazardSpec {
  const latest = hazards.spawns.reduce<CritterRelease | undefined>(
    (best, spawn) => (best === undefined || spawn.atMs >= best.atMs ? spawn : best),
    undefined,
  );
  return {
    ...hazards,
    spawns: [{ ...latest, type: 'squirrel', atMs: CRITTER_AT_MS }],
  };
}
